import { motion } from "framer-motion";
import { X, Phone, Home, Car, Users, Clock } from "lucide-react";

const painPoints = [
  {
    icon: Phone,
    title: "No Cold Calling",
    description: "Stop dialing random leads. Your clients come to you pre-approved and ready to buy.",
  },
  {
    icon: Home,
    title: "No Open Houses",
    description: "Weekends are yours. No more standing in empty houses hoping for walk-ins.",
  },
  {
    icon: Car,
    title: "No Driving to Show Homes",
    description: "Showing assistants handle in-person tours. Stay productive, not on the road.",
  },
  {
    icon: Users,
    title: "No Endless Networking",
    description: "Forget the breakfast meetings and chamber events. Focus on closing, not schmoozing.",
  },
  {
    icon: Clock,
    title: "No Begging Friends & Family",
    description: "Build a real business without pressuring your personal network for referrals.",
  },
];

const PainPointsSection = () => {
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-24 md:py-32 relative bg-white" data-testid="pain-points-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-red-600 mb-4 block">
            What We Eliminate
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            The Traditional Model is{" "}
            <span className="gradient-text">Broken</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            The real estate and mortgage industries are notoriously difficult to break into.
            CityWorth was built to remove every barrier standing in your way.
          </p>
        </motion.div>

        {/* Pain points grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {painPoints.map((point, index) => (
            <motion.div
              key={point.title}
              variants={itemVariants}
              transition={{ duration: 0.5 }}
              className={`relative group ${index === 0 ? "lg:col-span-2" : ""}`}
              data-testid={`pain-point-${index}`}
            >
              <div className="h-full bg-white rounded-2xl p-6 md:p-8 card-hover border border-slate-200 hover:border-red-300 transition-colors shadow-sm">
                {/* X mark indicator */}
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <X className="w-4 h-4 text-red-500" />
                </div>
                
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-6 group-hover:bg-red-100 transition-all">
                  <point.icon className="w-7 h-7 text-slate-500 group-hover:text-red-500 transition-colors" />
                </div>

                {/* Content */}
                <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-3">
                  {point.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {point.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-slate-50 rounded-2xl px-8 py-6 border border-slate-200">
            <p className="text-xl md:text-2xl font-medium text-slate-800 mb-2">
              "Many hardworking people struggle to gain traction and begin to doubt their long-term success."
            </p>
            <p className="text-slate-600">
              CityWorth was built to change that.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PainPointsSection;
